import { useEffect, useState } from "react";

import { countdownParts, type CountdownParts } from "@/utils/live-countdown.utils";

/**
 * A countdown that actually counts down.
 *
 * `countdownParts` is pure — it splits the distance between two instants, and
 * one of those instants has to come from somewhere. Calling it as
 * `countdownParts(date, new Date())` inside a component reads the clock once,
 * at render, and then never again: the numbers are correct on arrival and
 * frozen from that moment on, so the seconds only ever move when something else
 * happens to re-render the page. That is what a client saw on the closing card
 * — a timer that advanced on refresh.
 *
 * The tick belongs in a hook rather than in each component because there were
 * two of these and only one of them was live.
 *
 * Returns `null` for a null target so a caller can render nothing without
 * calling the hook conditionally.
 */
export function useLiveCountdown(target: Date | null | undefined): CountdownParts | null {
  const [now, setNow] = useState(() => new Date());
  // A `Date` is a fresh object on every render even when the underlying instant
  // is unchanged, so the effect keys off its numeric value. Depending on the
  // object itself would tear down and rebuild the interval once a second.
  const targetTime = target ? target.getTime() : null;

  useEffect(() => {
    if (targetTime === null || Number.isNaN(targetTime)) return;
    // Re-read on mount as well as on the interval: `now` was seeded when the
    // component first mounted, which may be a long scroll ago.
    setNow(new Date());
    const timer = window.setInterval(() => setNow(new Date()), 1_000);
    return () => window.clearInterval(timer);
  }, [targetTime]);

  if (targetTime === null || Number.isNaN(targetTime)) return null;
  return countdownParts(target as Date, now);
}
